<template>
    <div class="af-form">
        <div
            class="afu-w-full afu-p-3 sm:afu-p-6 afu-bg-white afu-rounded-md afu-m-auto sm:afu-max-w-md afu-w-100 afu-mt-2 afu-mb-2"
            :class="{ 'afu-shadow-lg': !isModal }"
        >
            <div class="af-form">
                <ValidationObserver
                    ref="saveSearchForm"
                    tag="form"
                    @submit.stop.prevent="onSubmit"
                >
                    <C1FormGroup>
                        <div class="afu-my-4">
                            <C1Label
                                label-for="Keywords"
                                class="afu-text-gray-700 afu-flex afu-justify-between"
                            >
                                <span>{{ $tc('label.keyword', 2) }}</span>
                            </C1Label>
                            <C1FormInput
                                id="search"
                                v-model="form.filter.search_keywords"
                                type="text"
                                placeholder="Keywords"
                            />
                        </div>
                        <div class="afu-my-4">
                            <C1Label
                                label-for="Classification"
                                class="afu-text-gray-700 afu-flex afu-justify-between"
                            >
                                <span>{{ $t('label.classification') }}</span>
                            </C1Label>
                            <ClassificationSelecter
                                label-inside="false"
                                @updateClassification="updateClassification"
                            />
                        </div>

                        <div class="afu-my-4">
                            <C1Label
                                label-for="Location"
                                class="afu-text-gray-700 afu-flex afu-justify-between"
                            >
                                <span>{{ $t('label.location') }}</span>
                            </C1Label>
                            <C1Typeahead
                                v-model="form.filter.location.label"
                                class="searchbar-typeahead w-100 afu-block focus:afu-outline-none afu-appearance-none afu-w-full afu-transition afu-duration-150 afu-ease-in-out afu-text-black afu-placeholder-custom afu-text-7xl afu-font-normal afu-bg-white afu-h-10"
                                :is-busy="spinner.location"
                                :options="locationOptions"
                                :no-icon="true"
                                :search-min-length="2"
                                label-key="label"
                                value-key="id"
                                filter-by="label"
                                :placeholder="$t('label.select_location')"
                                @search="searchLocations"
                                @hit="onLocationSelect"
                            >
                                <template slot="noResults">
                                    <div>
                                        No matching locations found.
                                    </div>
                                </template>
                            </C1Typeahead>
                        </div>

                        <C1FormGroup>
                            <C1Label
                                label-for="Salary Type"
                                class="afu-text-gray-700 afu-flex afu-justify-between"
                            >
                                <span>Salary</span>
                            </C1Label>
                            <ValidationProvider
                                v-slot="{ errors }"
                                name="Salary Type"
                                rules="required"
                            >
                                <C1Select
                                    v-model="salary.selectedType"
                                    label=""
                                    label-for="filterBySalary"
                                    :options="salary.types"
                                    placeholder="Select salary type"
                                    :is-invalid="!!errors[0]"
                                    :invalid-feedback="errors[0]"
                                />
                            </ValidationProvider>
                        </C1FormGroup>

                        <div v-if="isSelectedSalaryType">
                            <C1FormGroup>
                                <div class="afu-flex afu-items-start">
                                    <ValidationProvider
                                        v-slot="{ errors }"
                                        name="min"
                                        rules="required|numeric|lessthan:@max"
                                    >
                                        <C1FormInput
                                            id="minSalary"
                                            v-model="salary.min"
                                            placeholder="Min"
                                            :is-invalid="!!errors[0]"
                                            :invalid-feedback="errors[0]"
                                            @input="updateSalary"
                                            @blur="updateSalary"
                                        >
                                            <template v-slot:icon>
                                                {{ $t('global.currency_symbol') }}
                                            </template>
                                        </C1FormInput>
                                    </ValidationProvider>
                                    <FontAwesomeIcon
                                        :icon="['fal', 'minus']"
                                        size="xs"
                                        class="afu-mx-2 afu-mt-4 afu-text-gray-400"
                                    />
                                    <ValidationProvider
                                        v-slot="{ errors }"
                                        name="max"
                                        rules="required|numeric|greaterthan:@min"
                                    >
                                        <C1FormInput
                                            id="maxSalary"
                                            v-model="salary.max"
                                            placeholder="Max"
                                            :is-invalid="!!errors[0]"
                                            :invalid-feedback="errors[0]"
                                            @input="updateSalary"
                                            @blur="updateSalary"
                                        >
                                            <template v-slot:icon>
                                                {{ $t('global.currency_symbol') }}
                                            </template>
                                        </C1FormInput>
                                    </ValidationProvider>
                                </div>
                            </C1FormGroup>
                        </div>

                        <div class="afu-my-4">
                            <SearchAttributesGroup
                                v-if="showCustomAttribute(1)"
                                attribute-code="applyflow_custom_1"
                                :attribute-label="getCustomLabel(1)"
                                :options="customAttributeOptions(1)"
                                @handleSelect="handleSelect"
                            />
                        </div>

                        <div class="afu-my-4">
                            <SearchAttributesGroup
                                v-if="showCustomAttribute(2)"
                                attribute-code="applyflow_custom_2"
                                :attribute-label="getCustomLabel(2)"
                                :options="customAttributeOptions(2)"
                                @handleSelect="handleSelect"
                            />
                        </div>

                        <div class="afu-my-4">
                            <SearchAttributesGroup
                                v-if="showCustomAttribute(3)"
                                attribute-code="applyflow_custom_3"
                                :attribute-label="getCustomLabel(3)"
                                :options="customAttributeOptions(3)"
                                @handleSelect="handleSelect"
                            />
                        </div>

                        <div class="afu-my-4">
                            <SearchAttributesGroup
                                v-if="showCustomAttribute(4)"
                                attribute-code="applyflow_custom_4"
                                :attribute-label="getCustomLabel(4)"
                                :options="customAttributeOptions('applyflow_custom_4')"
                                @handleSelect="handleSelect"
                            />
                        </div>
                    </C1FormGroup>

                    <C1Alert
                        v-if="!!response.message"
                        :variant="response.variant"
                    >
                        <span v-html="response.message" />
                    </C1Alert>

                    <div class="afu-text-center">
                        <MqLayout mq="sm">
                            <C1Button
                                block
                                :disabled="loader.updating"
                                :is-busy="loader.updating"
                                type="submit"
                                variant="primary"
                                size="lg"
                            >
                                SEARCH
                            </C1Button>
                        </MqLayout>
                        <MqLayout mq="tsm+">
                            <C1Button
                                :disabled="loader.updating"
                                :is-busy="loader.updating"
                                type="submit"
                                variant="primary"
                                size="lg"
                            >
                                SEARCH
                            </C1Button>
                        </MqLayout>
                    </div>
                </ValidationObserver>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapActions } from 'vuex';
    import '@/plugins/vee-validate';
    import '../plugins/vee-validate';
    import { ValidationObserver, ValidationProvider } from 'vee-validate';
    import isEmpty from 'lodash/isEmpty';
    import C1Button from '@/components/C1Components/C1Button';
    import C1FormInput from '@/components/C1Components/C1FormInput';
    import C1Alert from '@/components/C1Components/C1Alert';
    import C1Select from '@/components/C1Components/C1Select';
    import C1FormGroup from '@/components/C1Components/C1FormGroup';
    import C1Label from '@/components/C1Components/C1Label';
    import SearchAttributesGroup from './SearchAttributesGroup';
    import ClassificationSelecter from '@/components/C1Components/ClassificationSelecter';
    import C1Typeahead from '@/components/C1Components/C1Typeahead';

    export default {
        name: 'AdvancedSearch',

        components: {
            ValidationObserver,
            ValidationProvider,
            C1Label,
            C1Button,
            C1FormInput,
            C1Alert,
            C1Select,
            C1FormGroup,
            SearchAttributesGroup,
            ClassificationSelecter,
            C1Typeahead,
        },

        props: {
            isModal: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                response: {
                    variant: '',
                    message: '',
                },
                loader: {
                    updating: false,
                },
                attributeOptions: [],
                locationOptions: [],
                form: {
                    filter: {
                        resultsPerPage: 20,
                        page: 1,
                        search_keywords: '',
                        location: {},
                        classifications: [],
                        pay_min: {},
                        pay_max: {},
                        applyflow_custom_1: [],
                        applyflow_custom_2: [],
                        applyflow_custom_3: [],
                        applyflow_custom_4: [],
                    },
                    search_url: null,
                },
                salary: {
                    types: [
                        {
                            id: 'annual_salary',
                            label: 'Annual salary',
                        },
                        {
                            id: 'hourly_rate',
                            label: 'Hourly rate',
                        },
                        {
                            id: 'per_day',
                            label: 'Per day',
                        },
                    ],
                    min: '',
                    max: '',
                    selectedType: {},
                },
                spinner: {
                    location: false,
                },
                submited: false,
            };
        },

        computed: {
            isSelectedSalaryType() {
                return !isEmpty(this.salary.selectedType);
            },
        },

        mounted() {
            this.getSiteAttributes();
        },

        methods: {
            ...mapActions('attribute', {
                $_getSiteAttributes: 'getSiteAttributes',
            }),

            ...mapActions('search', {
                $_searchJobsByFilter: 'searchJobsByFilter',
            }),

            ...mapActions('location', {
                $_getLocations: 'getLocations',
            }),

            updateClassification(classifications) {
                this.form.filter.classifications = classifications;
            },

            async getSiteAttributes() {
                await this.$_getSiteAttributes().then(response => {
                    this.attributeOptions = response.data;
                });
            },

            customAttributeOptions(index) {
                return this.attributeOptions[`applyflow_custom_${index}`];
            },

            getCustomLabel(index) {
                return (
                    window.afConfig[`applyflow_custom_${index}_label`] ||
                    `Custom Label ${index}`
                );
            },

            showCustomAttribute(index) {
                if (window.afConfig[`applyflow_custom_${index}_show`]) {
                    return (
                        window.afConfig[`applyflow_custom_${index}_show`] === 'Yes'
                    );
                } else {
                    return false;
                }
            },

            updateSalary() {
                if (this.salary.selectedType.id) {
                    this.form.filter.pay_min = {
                        value: this.salary.min,
                        pay_type_code: this.salary.selectedType.id,
                    };

                    this.form.filter.pay_max = {
                        value: this.salary.max,
                        pay_type_code: this.salary.selectedType.id,
                    };
                }
            },

            handleSelect(item) {
                this.form.filter[item.attribute] = item.selected;
            },

            searchLocations(str) {
                this.spinner.location = true;
                const keyword = str.toLowerCase();

                this.$_getLocations({ query: keyword }).then(res => {
                    const searchResults = res.data;
                    this.locationOptions = [...searchResults];
                    this.spinner.location = false;
                });
            },

            onLocationSelect(location) {
                this.form.filter.location = location;
            },

            async onSubmit() {
                const validate = await this.$refs.saveSearchForm.validate();
                if (!validate) {
                    return;
                }

                this.loader.updating = true;

                if (!isEmpty(this.salary.selectedType)) {
                    this.form.filter.display_pay_type = this.salary.selectedType.id;
                }

                this.form.filter.facet = 1;
                const data = this.form.filter;

                this.$_searchJobsByFilter(data)
                    .then(response => {
                        if (response.meta.canonical_url) {
                            this.$router.push(`/${response.meta.canonical_url}/`);
                        } else {
                            this.$router.push('/jobs');
                        }
                    })
                    .catch(e => {
                        if (
                            e.response.status === 404 ||
                            e.response.status === 422
                        ) {
                            window.location.href = '/not-found/';
                        }
                    })
                    .finally(() => {
                        this.loader.updating = false;
                    });
            },
        },
    };
</script>
<style lang="scss" scoped>
    .searchbar-typeahead {
        &::v-deep {
            .af-input-icon-x {
                top: 55%;
            }

            // .af-input {
            //     border: 0;
            // }
        }
    }
</style>

